/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC} from 'react';
import {makeStyles} from '@material-ui/core';
import {StylesProvider} from '@material-ui/core/styles';
import classNames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  CurrentDayProps,
  DatePicker,
  DatePickerTextConfig
} from './DatePicker';

// defines the styles used by the component
const useStyles = makeStyles(theme => ({
  datePickerContainer : {
    position : 'relative'
  },
  datePicker : {
    position : 'relative',
    zIndex   : dlsZIndex('datePickerExporterDatePicker')
  },
  dateBackdrop : {
    position : 'fixed',
    height   : '100vh',
    width    : '100vw',
    bottom   : 0,
    zIndex   : dlsZIndex('datePickerExporterDateBackdrop'),
    right    : 0,
    left     : 0,
    top      : 0
  }
}));

// defines the prop definitions for the component
export interface DatePickerExporterProps extends DatePickerTextConfig, CurrentDayProps {
  open: boolean;
  toggle: () => void;
  onChange: (date: Date| string, required: boolean) => void;
  closeOnClickOutside?: boolean;
  wrapperClassName?: string;
  isDate?: boolean;
  isTime?: boolean;
  isDateTime?: boolean;
  fieldLabel?: string;
  placeholder?: string;
  hasError?: boolean;
  errorText?: string;
  defaultValue?: Date;
  disabled?: boolean;
  required?: boolean;
  manualInput?: boolean;
  isClock?: boolean;
  isDatePicker?: boolean;
  validDateTimeFormats?: string[];
  validTimeFormats?: string[];
  allowManualEntry?: boolean;
  hideClearButton?: boolean;
  overridePopupStyle?: GenericObject;
}

/**
 * @DatePickerExporter
 *
 * Defines the DatePickerExporter component. This component exports
 * the DatePicker component.
 *
 * ```typescript
 *
 *   <DatePicker open={}
 *     toggle={}
 *     onChange={} />
 *
 * ```
 */
export const DatePickerExporter: FC<DatePickerExporterProps> = (
  props: DatePickerExporterProps
) => {
  const classes = useStyles();

  const {
    closeOnClickOutside,
    wrapperClassName,
    toggle,
    open
  } = props;

  function handleToggle() {
    if (!closeOnClickOutside) {
      return;
    }

    toggle();
  };

  function handleKeyPress(event: any) { event?.key === 'Escape' && handleToggle(); }

  const wrapperClasses = classNames(classes.datePicker, wrapperClassName);
  return (
    <StylesProvider>
      <div className={classes.datePickerContainer}>
        {
          open && (
            <div className={classes.dateBackdrop}
              onKeyPress={handleKeyPress}
              onClick={handleToggle} />
          )
        }
        <div className={wrapperClasses}>
          <DatePicker {...props} />
        </div>
      </div>
    </StylesProvider>
  );
};

export default DatePickerExporter;
